/**
 * Filter keys carrying a direct identifier, never written to the address bar.
 * Opaque UUID identifiers are deliberately excluded so deep links keep working.
 * Stored normalized so one entry covers `name_or_email`, `nameOrEmail` and `Name_Or_Email`.
 */
const normalizeKey = (key: string): string => key.toLowerCase().replace(/[^a-z0-9]/g, '');

export const PII_QUERY_PARAM_DENYLIST = new Set(
  [
    'corporate_email',
    'date_of_birth',
    'dob',
    'email',
    'employee_id',
    'first_name',
    'last_name',
    'name',
    'name_or_email',
    'phone_number',
    // The SSO redirect's single-use credentials, which must not reach Datadog.
    'relay_otp',
    'relay_token',
  ].map(normalizeKey)
);

export const isPiiQueryParam = (key: string): boolean => PII_QUERY_PARAM_DENYLIST.has(normalizeKey(key));

export const omitPiiQueryParams = <T extends Record<string, unknown>>(params: T): Partial<T> =>
  Object.fromEntries(Object.entries(params).filter(([key]) => !isPiiQueryParam(key))) as Partial<T>;

export const hasPiiQueryParam = (query: string): boolean => {
  for (const key of new URLSearchParams(query).keys()) {
    if (isPiiQueryParam(key)) {
      return true;
    }
  }

  return false;
};

export const omitPiiFromQueryString = (query: string): string => {
  const params = new URLSearchParams(query);

  for (const key of [...params.keys()]) {
    if (isPiiQueryParam(key)) {
      params.delete(key);
    }
  }

  return params.toString();
};
